import { getCurrentInstance, ref } from 'vue';

import { AuthService } from '@/services';

/**
 * Runs once after login to decide whether to show the MFA nudge or the
 * backup-code-exhausted alert. Call from App.vue (or a wrapper) when the
 * user object first appears on a `requireLogin` route.
 *
 * Returns `{ showNudge, showBackupAlert, check, dismiss, close, reset }`.
 */
export function useMfaPostLoginCheck() {
    const { proxy } = getCurrentInstance();

    const showNudge = ref(false);
    const showBackupAlert = ref(false);
    let checked = false;

    async function check() {
        if (checked) return;
        checked = true;

        // A user who just signed up is checked too: App.vue holds the check
        // on the workspace form, so the nudge first appears on the dashboard
        // after the workspace is created (TASK-14318, 10-07).
        const mfaLoginMethod = proxy.$store.state.user.mfaLoginMethod;

        let methods, nudge;
        try {
            [methods, nudge] = await Promise.all([
                AuthService.getMfaMethods(),
                AuthService.getMfaSetupNudge(),
            ]);
        } catch {
            return;
        }

        const hasMfa = methods.methods?.length > 0;
        const backupCount = methods.backup_code_count ?? 0;

        if (mfaLoginMethod === 'backup_code' && hasMfa && backupCount === 0) {
            showBackupAlert.value = true;
            return;
        }

        if (!hasMfa && !nudge.mfaSetupNudgeDismissed) {
            showNudge.value = true;
        }
    }

    async function dismiss() {
        try {
            await AuthService.dismissMfaSetupNudge();
        } catch {
            // best-effort
        }
        showNudge.value = false;
    }

    function close() {
        showNudge.value = false;
        showBackupAlert.value = false;
    }

    // Once per sign-in: the next sign-in in the same tab checks again.
    function reset() {
        checked = false;
        close();
    }

    return { showNudge, showBackupAlert, check, dismiss, close, reset };
}
